import { useState } from 'react'
import { Bot, Rocket, Loader2 } from 'lucide-react'
import { useT } from '../../hooks/useTranslation'
import { useHaptic } from '../../hooks/useHaptic'
import { useDashboardStore } from '../../store/dashboardStore'
import { botApi } from '../../services/botApi'

// Карточка состояния ПРОБНОГО бота на Дашборде (п.0.3, 2026-08-31). «Экран 3»
// онбординг-флоу: после автосоздания юзер видит Дашборд с пробным ботом и,
// если автоматический старт не сработал, — CTA «Запустить». Пока активна
// триал-фаза (есть виртуальный бот, подписки нет). После подписки карточка
// исчезает (обычный дашборд), после 2 сделок — плашка «Демо завершено».
//
// Fallback (task.txt): пока подходящей выплаты фандинга нет, показываем
// «Бот ждёт выплату фандинга. Как только выплата придёт — бот войдёт в сделку»
// вместо пустого экрана. (2026-09-15: прежняя формулировка «ждёт неэффективность
// рынка» заменена по просьбе владельца — бот работает именно по выплатам.)
export default function TrialStatusCard() {
  const t = useT()
  const haptic = useHaptic()
  const userBots           = useDashboardStore(s => s.userBots)
  const updateUserBot      = useDashboardStore(s => s.updateUserBot)
  const demoAllowed        = useDashboardStore(s => s.demoAllowed)
  const realAllowed        = useDashboardStore(s => s.realAllowed)
  const virtualOrdersUsed  = useDashboardStore(s => s.virtualOrdersUsed)
  const virtualOrdersLimit = useDashboardStore(s => s.virtualOrdersLimit)
  const setActiveTab       = useDashboardStore(s => s.setActiveTab)
  const setActiveModal     = useDashboardStore(s => s.setActiveModal)

  const [starting, setStarting] = useState(false)

  const virtualBot = Array.isArray(userBots) ? userBots.find(b => b.virtual) : null
  // Карточка только для триал-фазы: есть пробный бот, активной подписки нет.
  if (!virtualBot || demoAllowed || realAllowed) return null

  const finished = virtualOrdersUsed >= virtualOrdersLimit
  const running  = virtualBot.status === 'active'

  const handleStart = async () => {
    if (starting) return
    setStarting(true)
    haptic.medium()
    try {
      await botApi.startBot(virtualBot.id)
      updateUserBot(virtualBot.id, { status: 'active' })
      haptic.success()
    } catch (err) {
      const tg = window.Telegram?.WebApp
      const m = String(err?.message || '').match(/"detail"\s*:\s*"([^"]+)"/)
      const message = m ? m[1] : err?.message
      if (message) { if (tg?.showAlert) tg.showAlert(`⚠️ ${message}`); else alert(`⚠️ ${message}`) }
    } finally {
      setStarting(false)
    }
  }

  const openBots = () => { haptic.light(); setActiveTab('bots') }

  // ── Плашка «Демо завершено. Оформите подписку» (2 сделки исчерпаны) ───────
  if (finished) {
    return (
      <div style={{
        background: 'linear-gradient(135deg, rgba(255,107,53,0.10) 0%, rgba(255,59,48,0.06) 100%)',
        border: '1px solid rgba(255,107,53,0.25)',
        borderRadius: 16, padding: 16, marginBottom: 16,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 26 }}>🔒</div>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{t.virtualTrialFinishedTitle}</div>
        <div style={{ fontSize: 12.5, color: '#8E8E93', lineHeight: 1.5, maxWidth: 280 }}>
          {t.virtualTrialFinishedDesc}
        </div>
        <button
          onClick={() => { haptic.light(); setActiveModal('subscription') }}
          style={{
            marginTop: 6, padding: '12px 26px', borderRadius: 12,
            fontSize: 14, fontWeight: 700,
            background: 'linear-gradient(135deg, #FF6B35 0%, #FF3B30 100%)',
            color: '#fff', border: 'none', cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {t.virtualSubscribeBtn}
        </button>
      </div>
    )
  }

  // ── Пробный бот создан: запустить / запущен ───────────────────────────────
  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(16,185,129,0.10) 0%, rgba(5,150,105,0.05) 100%)',
      border: '1px solid rgba(16,185,129,0.25)',
      borderRadius: 16, padding: 16, marginBottom: 16,
      display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 42, height: 42, borderRadius: 12, flexShrink: 0,
          background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Bot size={22} color="#fff" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{t.trialStatusTitle}</div>
          <div style={{ fontSize: 12, color: '#8E8E93', marginTop: 2, lineHeight: 1.4 }}>
            {running ? t.trialStatusRunning : t.trialStatusStartDesc}
          </div>
        </div>
        {running && (
          <span style={{
            flexShrink: 0, display: 'flex', alignItems: 'center', gap: 5,
            fontSize: 11, fontWeight: 700, color: '#34D399',
            background: 'rgba(52,211,153,0.12)',
            border: '1px solid rgba(52,211,153,0.3)',
            borderRadius: 20, padding: '4px 10px',
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#34D399', boxShadow: '0 0 6px rgba(52,211,153,0.8)' }} />
            {running ? 'ON' : ''}
          </span>
        )}
      </div>

      {/* Fallback (task.txt): что видит юзер, пока фандингов нет */}
      <div style={{
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 12, padding: '10px 12px',
        fontSize: 12.5, color: '#8E8E93', lineHeight: 1.5,
      }}>
        {running ? t.trialStatusWaiting : t.onbCreatingHint}
      </div>

      {running ? (
        <button
          onClick={openBots}
          style={{
            width: '100%', padding: '12px', borderRadius: 12,
            fontSize: 14, fontWeight: 700,
            background: 'rgba(255,255,255,0.05)',
            color: '#D0D0D5', border: '1px solid rgba(255,255,255,0.1)',
            cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
        >
          {t.trialStatusViewBot}
        </button>
      ) : (
        <button
          onClick={handleStart}
          disabled={starting}
          style={{
            width: '100%', padding: '12px', borderRadius: 12,
            fontSize: 14, fontWeight: 700,
            background: starting ? 'rgba(16,185,129,0.35)' : 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
            color: '#fff', border: 'none', cursor: starting ? 'default' : 'pointer',
            WebkitTapHighlightColor: 'transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            boxShadow: starting ? 'none' : '0 2px 8px rgba(74,222,128,0.3)',
          }}
        >
          {starting ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Rocket size={16} />}
          {starting ? '...' : t.trialStatusStart}
        </button>
      )}
    </div>
  )
}
